import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../../services/auth.service';
import type { AssignableRep } from '../board/leads-board.component';
import {
  orderedStages, StageCatalogEntry,
  stageIdOf, stageLabelOf, StageTransition,
} from '../../../shared/stage-catalog';
import { legalMoveCodes } from '../board/stage-moves';
import {
  LEAD_STAGES_URL, LEAD_STAGE_TRANSITIONS_URL,
  ROLLOFF_FACETS_URL, ROLLOFF_MAX_PAGE_SIZE,
  ROLLOFF_OPPORTUNITIES_URL, ROLLOFF_TRANSFER_EXPORT_URL, ROLLOFF_VOCABULARY,
  hireAddress, ROLLOFF_CONTAINER_TYPES_URL, RollOffContainerType,
  RollOffOpportunity, RollOffOpportunityPage, RollOffStageFacet,
  RollOffStageFacets,
} from './rolloff-opportunity.model';
import {
  MilestoneStop,
} from '../../../components/milestone-rail/milestone-rail.component';
import {
  DataFilterField, DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import { UndoJournal } from '../../../shared/undo-journal';
import { rollOffRuleFilterFields } from './rolloff-filters';
import {
  TypedConfirmationDialogComponent,
} from '../../../shared/ui/typed-confirmation-dialog/typed-confirmation-dialog.component';
import { rollOffStageColor } from './rolloff-stage-colors';
import {
  RollOffHireDialogComponent, RollOffHireDialogData,
} from './rolloff-hire-dialog.component';

/**
 * The roll-off board: one column per stage of the roll-off vocabulary, one
 * card per hire, and a drag that proposes a move rather than making one.
 *
 * ── WHAT THIS SHARES WITH THE LEAD BOARD, AND WHAT IT DOES NOT ────────────
 *
 * Shared, in `shared/stage-catalog.ts`: reading a catalog into columns,
 * printing the tenant's wording for a code, resolving the id to move by, and
 * computing where a card may go — including the rule that an empty
 * `transitions` list means unconstrained. Those four questions are the whole
 * of what the two boards genuinely have in common, and the lead board asks
 * them through the same functions.
 *
 * Not shared, on purpose:
 *
 *   - The board itself. The lead board carries row grouping, territories,
 *     contribution tiers, qualification, route fit, server facets, a CSV
 *     export, a second List view over the shared data table, an agent strip
 *     and a view switcher. A hire has a site, a container, a price and two
 *     dates. Folding these into one component means a flag per difference,
 *     and a component with a flag per difference is two components wearing
 *     one name.
 *
 *   - The drop-then-confirm state below. It is thirty lines and it is
 *     duplicated deliberately, because the two boards mean different things
 *     by a drop: on the lead board a drop into another rep's row is a
 *     REASSIGNMENT, and its handler has to decide which of the two writes was
 *     intended before it does anything. This board has one kind of drop.
 *     Generalising over that difference costs more than the thirty lines.
 *
 *   - Which targets are legal. The lead board removes every `won` stage from
 *     its own offer, because a lead is won by a contract being signed and
 *     dragging a card there would claim a sale nobody signed. Roll-off does
 *     NOT do that, and the difference is deliberate rather than an omission:
 *     `RollOffOpportunityLogic.ChangeStageAsync` says so at length. Nothing
 *     drives a roll-off stage off a document, so the same guard would not
 *     raise the bar for reaching Fulfillment — it would make it unreachable
 *     by any means. What stands in for it is the branch: Fulfillment is
 *     reachable only from Prepayment, only from Contract, only from
 *     Negotiation. So the declared edges are the whole rule here, and this
 *     board adds nothing to them.
 */
/** One stage move a person made and may take back: what moved, from where,
 *  to where, in the terms needed to say so and to do so. */
export interface RollOffUndoStep {
  id: number;
  siteName: string;
  fromCode: string;
  toCode: string;
}

@Component({
  selector: 'app-rolloff-board',
  templateUrl: './rolloff-board.component.html',
  styleUrls: ['./rolloff-board.component.css'],
})
export class RollOffBoardComponent implements OnInit {
  /** The collection asks its Workspace parent to open this hire's routed detail. */
  @Output() detailRequested = new EventEmitter<number>();

  /** The tenant's roll-off stages. Empty until the catalog lands. */
  stages: StageCatalogEntry[] = [];

  /**
   * The stage the rail is focused on, or null for all of them.
   *
   * Sent to the server as `leadStageId`, the way the Leads collection sends
   * its own focus - not applied to the cards already drawn. A board draws one
   * PAGE of hires, so narrowing what is on screen would answer with whatever
   * happened to be in that page and call it the stage.
   */
  stageFocus: string | null = null;

  /**
   * Whether a card shows more than its heading.
   *
   * "Show details" rather than "Card density": density names the mechanism,
   * and what somebody wants is the extra fields. The same control and the same
   * wording the Leads board carries.
   */
  density: 'compact' | 'detailed' = 'compact';

  // ── The filter bar ─────────────────────────────────────────────────────
  //
  // The same shared bar the Leads list carries, in the same grammar, answered
  // by the same kind of whitelist on the server (RollOffFilterFields). The
  // rules travel with BOTH requests the board makes - the page and the
  // per-column counts - so a column can never say it holds more than the
  // filter lets it show.

  filterFields: DataFilterField[] = [];
  filterValues: DataFilterValues = {};

  /** The tenant's container sizes, for the Container rule's dropdown. */
  containerTypes: RollOffContainerType[] = [];

  /** Rebuilt when the sizes, the stages or the roster arrive rather than
   *  computed in a getter: the template reads this every change-detection
   *  pass, and a fresh array each time would re-enter the filter bar's
   *  ngOnChanges for no reason. */
  private buildFilterFields(): void {
    this.filterFields = rollOffRuleFilterFields(this.containerTypes, this.stages, this.reps);
  }

  /** A rule was added, changed or removed on the bar. The narrowing is the
   *  server's, so this reloads. */
  applyFilters(values: DataFilterValues): void {
    this.filterValues = values;
    this.load();
  }

  // ── Undo: walking the move journal backwards ──────────────────────────
  //
  // An undo is a COMPENSATING MOVE, not an erasure: the same stage command
  // back the other way, with a note saying what it is, so the hire's history
  // keeps both steps. Nothing asks the server to forget. The same rule the
  // Leads board follows - and the same transition rule decides whether the
  // way back is open, because hires and leads share one on the server. Where
  // it is not, the server refuses in its own words and the card says so.

  readonly undo = new UndoJournal<RollOffUndoStep>();

  /** One retraction at a time, in order. */
  undoing = false;

  get canUndo(): boolean { return this.undo.canUndo; }
  get undoCount(): number { return this.undo.count; }

  get undoTooltip(): string {
    const last = this.undo.last;
    if (!last) return 'Nothing to undo';
    const rest = this.undoCount - 1;
    return `Move ${last.siteName} back to ${this.columnLabel(last.fromCode)}`
      + (rest > 0 ? ` (${rest} more move${rest > 1 ? 's' : ''} behind it)` : '');
  }

  undoLastMove(): void {
    const step = this.undo.last;
    if (!step || this.undoing) return;
    const backToId = stageIdOf(this.stages, step.fromCode);
    if (backToId == null) {
      // The stage it came from is no longer in the catalog. Holding the step
      // would pin the button on a move it can never make.
      this.undo.retract();
      return;
    }
    this.undoing = true;
    this.http.put<RollOffOpportunity>(
      `${ROLLOFF_OPPORTUNITIES_URL}/${step.id}/stage`,
      { leadStageId: backToId, note: `Undo: back to ${this.columnLabel(step.fromCode)}` })
      .subscribe({
        next: moved => {
          this.undoing = false;
          this.undo.retract();
          this.replace(moved);
        },
        error: err => {
          this.undoing = false;
          // The panel is where a refusal is rendered, so it opens on the card
          // it refused - with the server's own sentence, which names the two
          // columns as the tenant labelled them.
          const onBoard = this.items.find(hire => hire.id === step.id) ?? null;
          this.moveTarget = onBoard;
          this.moveTo = null;
          this.moveError = err?.error?.error ?? 'The move could not be undone.';
        },
      });
  }

  /**
   * The moves this vocabulary declares. EMPTY MEANS UNCONSTRAINED — see
   * `legalStageTargets`. Roll-off ships six edges, so in practice this is
   * populated; a tenant whose transitions were never seeded gets a board that
   * offers every column, which is the same board the lead vocabulary gets.
   */
  transitions: StageTransition[] = [];

  items: RollOffOpportunity[] = [];
  /** How many hires match the filter, which is not how many are loaded. */
  total = 0;

  /**
   * How many hires stand in each column, from the server, over the whole
   * filter — NOT over the page below.
   *
   * A board renders columns and the list returns one page ordered by id, so a
   * tenant with more hires than a page has columns whose cards are simply not
   * in it. Counting the loaded cards produces a number that agrees with what
   * is on screen and is therefore invisibly wrong: a column reading "0" when
   * it holds thirty-seven. These counts come from a separate query that pages
   * nothing, which is the only way the difference can be shown at all.
   */
  facets: RollOffStageFacet[] = [];

  loading = false;
  loadError: string | null = null;

  search = '';
  minedOnly = false;

  /**
   * One page, at the server's own ceiling — `RollOffReadLimits.MaxPageSize`.
   *
   * This said 200 until 2026-09-03 and the server clamped it to 100 without
   * telling anyone; the comment beside it claimed it was the ceiling. It is
   * now the number the server will actually honour, and what does not fit is
   * reported per column rather than guessed at.
   */
  readonly pageSize = ROLLOFF_MAX_PAGE_SIZE;

  /** Monotonic guard: a slow answer to an old filter never wins. */
  private loadSeq = 0;

  // ── The move preview ───────────────────────────────────────────────────
  //
  // A drop never moves the card. It opens this panel; the card sits where it
  // was until the server answers, and a refusal leaves it there with the
  // server's own words under it. There is no optimistic move to roll back
  // because there is no optimistic move.
  moveTarget: RollOffOpportunity | null = null;
  moveTo: string | null = null;
  moveNote = '';
  moveError: string | null = null;
  moving = false;

  /** Who a hire may be owned by. Empty when this account cannot read the
   *  roster, and then a card names its owner by id. */
  reps: AssignableRep[] = [];

  private myUserId: number | null = null;

  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe(); }

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Roll-off board'), name: 'Roll-off board', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ search: this.search, mine: this.minedOnly }),
      describe: state => JSON.stringify(state),
      restore: state => { this.search = state.search; this.minedOnly = state.mine; this.load(); },
    });
  }

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private dialog: MatDialog,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {}

  /** Creating, editing and moving a hire are all one key on the server. */
  get canManage(): boolean {
    return this.auth.hasPermission('LeadPipeline.Manage');
  }

  ngOnInit(): void {
    this.registerViewContext();
    this.myUserId = this.auth.tenant.currentUserId;

    // The columns, their wording and the branch: two reads, joined here.
    //
    // The stages are asked for by vocabulary; the edges are not, because the
    // endpoint returns every declared edge and `legalMoveCodes` scopes them by
    // the vocabulary of the stage in hand. That is the merged design's shape,
    // and it means one request serves both boards.
    forkJoin({
      stages: this.http.get<StageCatalogEntry[]>(
        LEAD_STAGES_URL, { params: { vocabulary: ROLLOFF_VOCABULARY } }),
      transitions: this.http.get<StageTransition[]>(LEAD_STAGE_TRANSITIONS_URL),
    })
      .subscribe({
        next: catalog => {
          this.stages = catalog.stages ?? [];
          this.transitions = catalog.transitions ?? [];
          // The Stage rule lists these, so the bar is rebuilt with them.
          this.buildFilterFields();
          this.load();
        },
        // The status separates "your role lacks a permission" from "the server
        // broke". Swallowing it has cost this product a real hunt before.
        error: (err: { status?: number }) => {
          this.loadError =
            `Failed to load the roll-off stages (HTTP ${err.status ?? '?'}).`;
        },
      });

    // The roster is the lead pipeline's, because it is the same reps. An
    // account that cannot read it simply sees owner ids - and a Sales rep
    // rule that offers only "Unassigned".
    this.http.get<AssignableRep[]>('/api/sales-routes/reps').subscribe({
      next: rows => { this.reps = rows ?? []; this.buildFilterFields(); },
      error: () => { /* no roster: cards name owners by id */ },
    });

    // The Container rule lists the tenant's sizes. A failed read leaves that
    // dropdown empty, which offers nothing rather than offering nonsense.
    this.buildFilterFields();
    this.http.get<RollOffContainerType[]>(ROLLOFF_CONTAINER_TYPES_URL).subscribe({
      next: sizes => { this.containerTypes = sizes ?? []; this.buildFilterFields(); },
      error: () => { /* the Container filter simply has nothing to offer */ },
    });
  }

  load(): void {
    this.loadError = null;
    this.loading = true;
    const seq = ++this.loadSeq;
    const params: Record<string, string> = {
      page: '1', pageSize: String(this.pageSize),
    };
    if (this.search.trim()) params['search'] = this.search.trim();
    if (this.minedOnly && this.myUserId != null)
      params['salesRepUserId'] = String(this.myUserId);
    // Deliberately absent from facetParams below: the facets ARE the per-stage
    // breakdown, and narrowing them to the focused stage would leave every
    // other stop on the rail reporting zero.
    if (this.stageFocus) {
      const id = this.stageIdOf(this.stageFocus);
      if (id != null) params['leadStageId'] = String(id);
    }
    // The filter bar's rules, on both requests below: the columns count the
    // same set the cards show, or a column claims more than it can draw. The
    // rep rule stays behind while "Assigned to me" narrows the same column, as
    // on the Leads board: sending both would AND them into an empty board
    // rather than answer either question.
    const rules = toRuleParams(
      this.filterValues, this.filterFields,
      this.minedOnly && this.myUserId != null ? ['salesRep'] : []);

    // The same filter, minus the paging, asked of the server as a per-column
    // tally. Sent as its own request rather than folded into the page because
    // it answers a different question: the page says what is on screen and
    // this says what there is.
    //
    // One spelling, `salesRepUserId`, because that is the only one either
    // endpoint binds. This used to send `owner` to the list and `ownerUserId`
    // to the facets, on the belief that the list took both. It never did: the
    // binder dropped the short one and the LIST came back unfiltered while the
    // counts above it were filtered - the two disagreeing on screen, with no
    // error to say so. Found when the column was renamed.
    const facetParams: Record<string, string | string[]> = {};
    if (params['search']) facetParams['search'] = params['search'];
    if (params['salesRepUserId']) facetParams['salesRepUserId'] = params['salesRepUserId'];
    if (rules.length) facetParams['rule'] = rules;
    this.http.get<RollOffStageFacets>(
      ROLLOFF_FACETS_URL, { params: facetParams }).subscribe({
      next: res => {
        if (seq !== this.loadSeq) return;
        this.facets = res?.columns ?? [];
      },
      // Silent: the page request below reports a load failure, and two
      // messages for one outage is noise. What this costs is the column
      // counters, and `columnTotal` falls back to the loaded cards.
      error: () => { if (seq === this.loadSeq) this.facets = []; },
    });

    this.http.get<RollOffOpportunityPage>(
      ROLLOFF_OPPORTUNITIES_URL,
      { params: rules.length ? { ...params, rule: rules } : params }).subscribe({
      next: page => {
        if (seq !== this.loadSeq) return;
        this.loading = false;
        this.items = page.items ?? [];
        this.total = page.total ?? 0;
      },
      error: (err: { status?: number }) => {
        if (seq !== this.loadSeq) return;
        this.loading = false;
        this.loadError = `Failed to load roll-off hires (HTTP ${err.status ?? '?'}).`;
      },
    });
  }

  // -- The table's own actions, on the board that draws its rows ---------
  //
  // Export, Import and Find duplicates come from the shared component the
  // Table view carries: one endpoint handed over, and no second copy of the
  // buttons. Delete All is this page's own, because it is not a table action
  // at all - it empties the account's deals, it is gated on Company.Manage
  // rather than on the pipeline keys, and nothing undoes it.

  readonly exportUrl = ROLLOFF_TRANSFER_EXPORT_URL;

  /** The account-level key, as on the Leads band. A rep who may move every
   *  card on this board still may not empty the table. */
  get canDeleteAllData(): boolean {
    return this.auth.hasPermission('Company.Manage');
  }

  deletingAll = false;
  deleteAllNotice: string | null = null;
  deleteAllError: string | null = null;

  /**
   * Every deal in the account, gone - once the word has been typed.
   *
   * The shared typed dialog rather than a yes/no, for the reason the Leads
   * band uses it: this is irreversible and one click from a menu somebody
   * opened for something else. The server asks for the same word again and
   * refuses without it, so a screen that stopped asking could not do this by
   * accident either.
   */
  deleteAllDeals(): void {
    if (this.deletingAll) return;
    this.deleteAllNotice = null;
    this.deleteAllError = null;
    this.dialog.open(TypedConfirmationDialogComponent, {
      data: {
        title: 'Delete all roll-off deal data',
        message: 'This permanently deletes every roll-off deal in this account. '
          + 'This cannot be undone.',
        confirmationText: 'DELETE',
        confirmLabel: 'Delete All',
      },
    }).afterClosed().subscribe((confirmed?: boolean) => {
      if (!confirmed) return;
      this.deletingAll = true;
      this.http.delete<{ deletedCount: number }>(ROLLOFF_OPPORTUNITIES_URL, {
        body: { confirmation: 'DELETE' },
      }).subscribe({
        next: result => {
          this.deletingAll = false;
          const count = result?.deletedCount ?? 0;
          this.deleteAllNotice = `Deleted ${count} deal${count === 1 ? '' : 's'}.`;
          // Read the board again rather than emptying it here: what the server
          // kept - a deal a contract still stands on - is its answer, not ours.
          this.load();
        },
        error: (err: { error?: { error?: string } }) => {
          this.deletingAll = false;
          this.deleteAllError =
            err?.error?.error ?? 'The deals could not be deleted.';
        },
      });
    });
  }

  /** "Assigned to me" is only offered when the token says who I am. */
  get canFilterMine(): boolean { return this.myUserId != null; }

  /** True when more hires match than the board is showing. */
  get truncated(): boolean { return this.total > this.items.length; }

  // ── Columns ────────────────────────────────────────────────────────────

  /**
   * The columns, from the catalog and in the tenant's order. Never a literal
   * list: a tenant may reorder these, rename them, deactivate one, or add a
   * stage of its own, and the board follows without a frontend change.
   */
  get columns(): StageCatalogEntry[] {
    return orderedStages(this.stages);
  }

  /** What to print above a column. */
  columnLabel(code: string): string {
    return stageLabelOf(this.stages, code);
  }

  stageColor(code: string): string {
    return rollOffStageColor(code);
  }

  /** Where the skip goes. Shown on a card only under "Show details": it is
   *  the longest thing a hire carries and would push the size, the price and
   *  the dates off a compact card. */
  address(hire: RollOffOpportunity): string {
    return hireAddress(hire);
  }

  /** Cards in a column, matched on the CODE the card carries. */
  itemsFor(code: string): RollOffOpportunity[] {
    return this.items.filter(hire => hire.leadStageCode === code);
  }

  /** How many cards this column is DRAWING. Not how many it holds. */
  columnCount(code: string): number {
    return this.itemsFor(code).length;
  }

  /**
   * How many hires this column holds, from the server.
   *
   * Never `itemsFor(code).length`. That is the number of cards that came back
   * in one page ordered by id across every column, and a tenant with more
   * hires than a page has columns whose cards are not in it — so counting the
   * loaded ones prints a number that matches the screen and misrepresents the
   * database.
   *
   * Falls back to the drawn cards only when the facet request failed, where
   * the choice is between an undercount and no count at all, and where
   * `columnIsTruncated` is false so nothing claims the column is complete.
   */
  columnTotal(code: string): number {
    const facet = this.facetFor(code);
    return facet ? facet.count : this.columnCount(code);
  }

  /**
   * The stages as the shared rail wants them.
   *
   * Counts come from the facets, which are asked WITHOUT the stage focus - so
   * the rail keeps reporting every stage while one of them is chosen. A rail
   * whose other stops fell to zero the moment you picked one would be a rail
   * that erases the map you navigate it by.
   */
  get railStops(): MilestoneStop[] {
    return this.columns.map(stage => ({
      id: stage.code,
      label: this.columnLabel(stage.code),
      count: this.columnTotal(stage.code),
      color: this.stageColor(stage.code),
    }));
  }

  /**
   * What "All" counts: every hire the current search and owner filter match,
   * across all stages.
   *
   * Summed from the facets rather than read from the list's own `total`.
   * `total` is the total OF THE CURRENT QUERY, so with a stage focused it is
   * that stage's count - and "All (12)" sitting above a focused stage that
   * also says 12 reads as though nothing else exists.
   */
  get railTotal(): number {
    return this.facets.length
      ? this.facets.reduce((sum, facet) => sum + facet.count, 0)
      : this.total;
  }

  /** Focus a stage, or clear back to all of them. Reloads, because the
   *  narrowing is the server's. */
  focusStage(code: string | null): void {
    this.stageFocus = code;
    this.load();
  }

  /** The catalog id behind a stage code - what the list endpoint binds. */
  private stageIdOf(code: string): number | null {
    return this.stages.find(stage => stage.code === code)?.id ?? null;
  }

  facetFor(code: string): RollOffStageFacet | undefined {
    return this.facets.find(facet => facet.column === code);
  }

  /** True when this column holds more than it is drawing. */
  columnIsTruncated(code: string): boolean {
    return this.columnHiddenCount(code) > 0;
  }

  /** How many of this column's hires did not fit in the loaded page. */
  columnHiddenCount(code: string): number {
    const facet = this.facetFor(code);
    if (!facet) return 0;             // no facet: say nothing rather than guess
    return Math.max(0, facet.count - this.columnCount(code));
  }

  /** The quoted value standing in a column, over the whole filter. Null when
   *  the facets are unavailable, so the header prints nothing rather than a
   *  total computed from a partial page. */
  columnQuoted(code: string): number | null {
    return this.facetFor(code)?.quotedTotal ?? null;
  }

  /** A finished column reads differently — asked of the outcome, never of the
   *  wording, because this vocabulary's winning stage is called Fulfillment. */
  isFinished(stage: StageCatalogEntry): boolean {
    return stage.outcome !== 'open';
  }

  // ── Drag: only legal targets ───────────────────────────────────────────

  /**
   * Where this card may go, from the edges the catalog declared. This is why
   * `transitions` is sent with the stages: one read answers it for every card
   * on the board, and the answer cannot go stale between cards.
   */
  availableTargets(hire: RollOffOpportunity): string[] {
    // `false`: a hire reaches Fulfillment because a person says the skip was
    // delivered. Nothing in this product could evidence that - the operations
    // application that would have known was retired - so the server does not
    // ask for evidence and neither does the board. The lead pipeline's default
    // is the opposite, and for good reason: a lead is won by a signed
    // contract.
    return legalMoveCodes(
      this.stages, this.transitions, hire.leadStageCode, false);
  }

  /**
   * The card currently in the air, or null. Set only so the columns can show
   * which of them will take it — the offer has to be visible DURING the drag,
   * not explained after it.
   */
  dragging: RollOffOpportunity | null = null;

  onDragStarted(hire: RollOffOpportunity): void { this.dragging = hire; }

  onDragEnded(): void { this.dragging = null; }

  /** Whether the column in the air's card may land here. False for the column
   *  it is already in, which is not a move. */
  isLegalTarget(code: string): boolean {
    return this.dragging != null && this.availableTargets(this.dragging).includes(code);
  }

  /** Whether this column is visibly refusing the card in the air. */
  isBlockedTarget(code: string): boolean {
    return this.dragging != null
      && this.dragging.leadStageCode !== code
      && !this.availableTargets(this.dragging).includes(code);
  }

  /**
   * A drop opens the confirmation the Move button opens. It does not move
   * anything, and it does not send anything.
   */
  onDrop(event: CdkDragDrop<string>, targetColumn: string): void {
    this.dragging = null;
    const hire = event.item.data as RollOffOpportunity;
    if (hire.leadStageCode === targetColumn) return;   // same column: not a move

    this.moveNote = '';
    this.moveTarget = hire;
    if (this.availableTargets(hire).includes(targetColumn)) {
      this.moveTo = targetColumn;                       // offer a one-click Confirm
      this.moveError = null;
    } else {
      // Refusing here rather than letting the server 409: the branch is
      // already on the client, and both columns are named the way this person
      // sees them — their own headings, not our stage codes.
      this.moveTo = null;
      this.moveError = `Can't move ${hire.leadStageName} to ${this.columnLabel(targetColumn)}.`;
    }
  }

  /** The Move button, and Enter on a focused card, open the same panel. */
  startMove(hire: RollOffOpportunity): void {
    if (!this.availableTargets(hire).length) return;
    const same = this.moveTarget?.id === hire.id;
    this.moveTarget = same ? null : hire;
    this.moveTo = null;
    this.moveError = null;
    this.moveNote = '';
  }

  /** Enter opens the panel only from the card itself: a keyup bubbling out of
   *  the note input or a Confirm button must not close the panel and swallow
   *  the error it is showing. */
  onCardEnter(event: Event, hire: RollOffOpportunity): void {
    if (event.target === event.currentTarget) this.startMove(hire);
  }

  cancelMove(): void {
    this.moveTarget = null;
    this.moveTo = null;
    this.moveError = null;
    this.moveNote = '';
  }

  /**
   * Send the move. The card changes column only here, and only from the hire
   * the server sends back — so a refusal cannot leave the board claiming
   * something the database does not say.
   */
  confirmMove(hire: RollOffOpportunity, to: string): void {
    const leadStageId = stageIdOf(this.stages, to);
    if (leadStageId == null) return;      // not a stage we know; nothing to send

    this.moveError = null;
    this.moving = true;
    this.http.put<RollOffOpportunity>(
      `${ROLLOFF_OPPORTUNITIES_URL}/${hire.id}/stage`,
      { leadStageId, note: this.moveNote.trim() || null }).subscribe({
      next: moved => {
        this.moving = false;
        // Recorded from the hire as it was BEFORE the move: `hire` still
        // carries the code it came from, and `moved` the one it went to.
        this.undo.record({
          id: hire.id, siteName: hire.siteName, fromCode: hire.leadStageCode, toCode: to,
        });
        this.replace(moved);
        this.moveTarget = null;
        this.moveTo = null;
        this.moveNote = '';
      },
      error: err => {
        this.moving = false;
        // The panel stays open, on the card, where it was. `err.error.error`
        // is the server's own sentence for a refused move — it names the two
        // columns as the tenant labelled them, which is better than anything
        // this screen could compose.
        this.moveError = err?.error?.error
          ?? (err?.status === 403
            ? 'You do not have permission to move roll-off deals.'
            : err?.status === 404
              ? 'That deal is gone, or that stage is no longer active.'
              : 'The move could not be saved.');
      },
    });
  }

  /** Swap a hire for the server's copy of it, in place. */
  private replace(hire: RollOffOpportunity): void {
    const at = this.items.findIndex(row => row.id === hire.id);
    if (at >= 0) this.items = [
      ...this.items.slice(0, at), hire, ...this.items.slice(at + 1),
    ];
    else this.load();
  }

  // ── Create / edit ──────────────────────────────────────────────────────

  openCreate(): void { this.openForm(null); }

  openEdit(hire: RollOffOpportunity): void { this.openForm(hire); }

  private openForm(hire: RollOffOpportunity | null): void {
    const data: RollOffHireDialogData = { hire, reps: this.reps };
    this.dialog
      .open(RollOffHireDialogComponent, { width: '560px', data })
      .afterClosed()
      .subscribe((saved?: RollOffOpportunity | null) => {
        if (!saved) return;
        // A create lands at the front of the vocabulary and is not in the
        // loaded page; an edit is. Reload for the first, patch for the second.
        if (this.items.some(row => row.id === saved.id)) this.replace(saved);
        else this.load();
      });
  }

  // ── Detail ─────────────────────────────────────────────────────────────

  openDetail(hire: RollOffOpportunity): void { this.detailRequested.emit(hire.id); }

  // ── Card fields ────────────────────────────────────────────────────────

  /**
   * What size to print on a card: the catalog's wording, or — for a hire whose
   * free text the 2026-09-03 backfill could not place — the phrase somebody
   * actually typed.
   *
   * The fallback is the point. "large", "roro" and "3.5t" are exactly the
   * values no normalisation could safely guess at, and they are also the ones
   * where a person had gone out of their way to write something down. A card
   * that showed nothing for those would look like a hire nobody had sized.
   */
  containerLabel(hire: RollOffOpportunity): string | null {
    const label = hire.containerTypeLabel?.trim();
    if (label) return label;
    const typed = hire.containerSizeText?.trim();
    return typed ? typed : null;
  }

  /** Whether that string is a phrase somebody typed rather than a catalog row.
   *  The card marks it, so nobody reads "roro" as a configured size. */
  isUnplacedSize(hire: RollOffOpportunity): boolean {
    return hire.containerTypeId == null && !!hire.containerSizeText?.trim();
  }

  /** A rep's name, or the id the card carries when the roster is unreadable. */
  ownerLabel(hire: RollOffOpportunity): string {
    if (hire.salesRepUserId == null) return 'Unassigned';
    const rep = this.reps.find(r => r.userId === hire.salesRepUserId);
    return rep?.displayName?.trim() || rep?.userName?.trim() || `Rep ${hire.salesRepUserId}`;
  }

  /** "Aug 12 – Aug 19", or one end, or nothing — dates are optional here. */
  hireDates(hire: RollOffOpportunity): string | null {
    const from = formatHireDate(hire.hireStartsOn);
    const to = formatHireDate(hire.hireEndsOn);
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Until ${to}`;
    return null;
  }

  trackByHire = (_: number, hire: RollOffOpportunity): number => hire.id;
  trackByColumn = (_: number, stage: StageCatalogEntry): string => stage.code;
}

/**
 * A `DateOnly` printed as a day, not as an instant.
 *
 * `new Date('2026-08-12')` is parsed as UTC midnight and then rendered in the
 * local zone, so west of Greenwich a hire starting on the 12th prints as the
 * 11th. The parts are read out of the string instead, because that is all a
 * `DateOnly` ever was.
 */
export function formatHireDate(value: string | null | undefined): string | null {
  const parts = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (!parts) return null;
  const local = new Date(+parts[1], +parts[2] - 1, +parts[3]);
  return local.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
